import { useEffect, useRef } from "react";
import { Client, type IFrame } from "@stomp/stompjs";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAuthStore } from "./auth";

const wsUrl = (() => {
  const apiUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8081";
  return apiUrl.replace(/^http/, "ws") + "/ws";
})();

/**
 * Decodes the `tnt` (tenantId) claim from a JWT without verifying — verification
 * is the server's job. We only use this client-side to know which topic to
 * subscribe to. If the token is bogus, the server will reject the CONNECT.
 */
function tenantIdFromToken(token: string): string | null {
  try {
    const payload = token.split(".")[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(decoded) as { tnt?: string };
    return claims.tnt ?? null;
  } catch {
    return null;
  }
}

/**
 * Hook that opens a STOMP-over-WebSocket session against booqly-api `/ws`,
 * subscribes to the operator's tenant payments + bookings + inventory topics,
 * and invalidates the relevant react-query caches whenever an outbox event
 * fires. The connection auto-reconnects on transient drops.
 *
 * Mount this once at the App level (not per-page).
 */
export function useRealtimeOperatorEvents() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const ref = useRef<Client | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    const tenantId = tenantIdFromToken(accessToken);
    if (!tenantId) return;

    const client = makeClient(accessToken, tenantId, qc);
    ref.current = client;
    client.activate();

    return () => {
      ref.current?.deactivate().catch(() => {});
      ref.current = null;
    };
  }, [accessToken, qc]);
}

function makeClient(token: string, tenantId: string, qc: QueryClient): Client {
  const client = new Client({
    brokerURL: wsUrl,
    connectHeaders: { Authorization: `Bearer ${token}` },
    reconnectDelay: 5_000,
    heartbeatIncoming: 20_000,
    heartbeatOutgoing: 20_000,
    debug: () => {}, // flip to console.log to debug

    onStompError: (frame: IFrame) => {
      console.warn("[realtime] STOMP error", frame.headers["message"], frame.body);
    },
  });

  client.onConnect = () => {
    const base = `/topic/tenant/${tenantId}`;

    // payment.* → verification queue + dashboard
    client.subscribe(`${base}/payments`, () => {
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["booking"] });
      qc.invalidateQueries({ queryKey: ["dashboard.overview"] });
    });

    // booking.* → bookings list + dashboard
    client.subscribe(`${base}/bookings`, () => {
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["dashboard.overview"] });
    });

    // inventory.* → inventory overview
    client.subscribe(`${base}/inventory`, () => {
      qc.invalidateQueries({ queryKey: ["inventory.overview"] });
    });
  };

  return client;
}
